import { usePage } from "@inertiajs/react"

import type { SharedProps } from "@/types"

import {
  CODE_TAKE_TYPE,
  type StoryFormData,
  type StoryTakeAttribute,
  type StoryTakesAttributes,
  type TakeMediaFilesAttributes,
  emptyTakeAttributes,
  isCodeTake,
  isVideoContentType,
  normalizeTakePositions,
  sortVisibleTakes,
} from "./story-form-utils"

export function useTakeManagement(
  data: StoryFormData,
  onChange: (value: StoryFormData) => void,
) {
  const { max_images_per_post } = usePage<SharedProps>().props

  const takeEntries = sortVisibleTakes(
    Object.entries(data.takes_attributes).map(([key, take]) => [
      Number(key),
      take,
    ]),
  )

  function nextTakeKey() {
    const keys = Object.keys(data.takes_attributes).map(Number)
    return keys.length === 0 ? 0 : Math.max(...keys) + 1
  }

  function nextMediaKey(takeKey: number) {
    const mediaKeys = Object.keys(
      data.takes_attributes[takeKey]?.take_media_files_attributes ?? {},
    ).map(Number)
    return mediaKeys.length === 0 ? 0 : Math.max(...mediaKeys) + 1
  }

  function updateTakes(nextTakes: StoryTakesAttributes) {
    onChange({ ...data, takes_attributes: normalizeTakePositions(nextTakes) })
  }

  function updateTake(
    takeKey: number,
    updater: (take: StoryTakeAttribute) => StoryTakeAttribute,
  ) {
    const take = data.takes_attributes[takeKey]
    if (!take) return

    updateTakes({
      ...data.takes_attributes,
      [takeKey]: updater(take),
    })
  }

  function addTake(type: StoryTakeAttribute["type"] = "Take") {
    const key = nextTakeKey()
    updateTakes({
      ...data.takes_attributes,
      [key]: {
        ...emptyTakeAttributes(type),
        position: takeEntries.length,
      },
    })
  }

  function removeTake(takeKey: number) {
    const take = data.takes_attributes[takeKey]
    if (!take) return

    if (take.id) {
      updateTakes({
        ...data.takes_attributes,
        [takeKey]: { ...take, _destroy: true },
      })
      return
    }

    const next = { ...data.takes_attributes }
    delete next[takeKey]
    updateTakes(next)
  }

  function moveTake(takeKey: number, direction: "up" | "down") {
    const visibleKeys = takeEntries.map(([key]) => key)
    const index = visibleKeys.indexOf(takeKey)
    const swapIndex = direction === "up" ? index - 1 : index + 1
    if (index < 0 || swapIndex < 0 || swapIndex >= visibleKeys.length) return

    const reorderedKeys = [...visibleKeys]
    ;[reorderedKeys[index], reorderedKeys[swapIndex]] = [
      reorderedKeys[swapIndex],
      reorderedKeys[index],
    ]

    const next = { ...data.takes_attributes }
    reorderedKeys.forEach((key, newIndex) => {
      next[key] = { ...next[key], position: newIndex }
    })
    updateTakes(next)
  }

  function changeTakeType(
    takeKey: number,
    nextType: StoryTakeAttribute["type"],
  ) {
    updateTake(takeKey, (currentTake) => ({
      ...currentTake,
      ...emptyTakeAttributes(nextType),
      id: currentTake.id,
      body: currentTake.body,
      position: currentTake.position,
      _destroy: currentTake._destroy,
      take_media_files_attributes: {},
    }))
  }

  function visibleImageCountForTake(
    take: StoryTakeAttribute,
    existingMediaFiles: {
      id: number
      content_type?: string | null
      url?: string | null
    }[],
  ) {
    const pendingDestroyIds = new Set(
      Object.values(take.take_media_files_attributes)
        .filter((attr) => attr._destroy)
        .map((attr) => attr.id),
    )
    const existingImageCount = existingMediaFiles.filter(
      (mediaFile) =>
        mediaFile.content_type?.startsWith("image/") &&
        mediaFile.url &&
        !pendingDestroyIds.has(mediaFile.id),
    ).length
    const newImageCount = Object.values(
      take.take_media_files_attributes,
    ).filter(
      (attr) => attr.media_file?.type.startsWith("image/") && !attr._destroy,
    ).length

    return existingImageCount + newImageCount
  }

  function handleFiles(
    takeKey: number,
    files: File[],
    existingMediaFiles: {
      id: number
      content_type?: string | null
      url?: string | null
    }[],
    onLimitMessage: (message?: string) => void,
    onImageAdded?: (takeKey: number, mediaKey: number, file: File) => void,
  ) {
    const take = data.takes_attributes[takeKey]
    if (!take || isCodeTake(take)) return

    const remainingImageSlots =
      max_images_per_post - visibleImageCountForTake(take, existingMediaFiles)
    const nextFiles: File[] = []
    let acceptedImagesCount = 0
    let skippedImagesCount = 0

    for (const file of files) {
      if (isVideoContentType(file.type)) {
        nextFiles.push(file)
        continue
      }

      if (
        file.type.startsWith("image/") &&
        acceptedImagesCount < remainingImageSlots
      ) {
        nextFiles.push(file)
        acceptedImagesCount += 1
        continue
      }

      if (file.type.startsWith("image/")) skippedImagesCount += 1
    }

    onLimitMessage(
      skippedImagesCount > 0
        ? `Maximum of ${max_images_per_post} images per take.`
        : undefined,
    )

    let mediaKey = nextMediaKey(takeKey)
    const additions: TakeMediaFilesAttributes = {}
    const addedImages: { mediaKey: number; file: File }[] = []
    for (const file of nextFiles) {
      const key = mediaKey++
      additions[key] = { media_file: file, alt_text: "" }
      if (file.type.startsWith("image/")) {
        addedImages.push({ mediaKey: key, file })
      }
    }

    updateTake(takeKey, (currentTake) => ({
      ...currentTake,
      take_media_files_attributes: {
        ...currentTake.take_media_files_attributes,
        ...additions,
      },
    }))

    if (onImageAdded) {
      for (const { mediaKey: key, file } of addedImages) {
        onImageAdded(takeKey, key, file)
      }
    }
  }

  function removeExistingMedia(takeKey: number, mediaId: number) {
    updateTake(takeKey, (currentTake) => {
      const mediaKey = nextMediaKey(takeKey)
      return {
        ...currentTake,
        take_media_files_attributes: {
          ...currentTake.take_media_files_attributes,
          [mediaKey]: { id: mediaId, _destroy: true },
        },
      }
    })
  }

  function removeNewMedia(takeKey: number, mediaKey: number) {
    updateTake(takeKey, (currentTake) => {
      const next = { ...currentTake.take_media_files_attributes }
      delete next[mediaKey]
      return { ...currentTake, take_media_files_attributes: next }
    })
  }

  function updateExistingAltText(
    takeKey: number,
    mediaId: number,
    altText: string,
  ) {
    updateTake(takeKey, (currentTake) => {
      const entry = Object.entries(
        currentTake.take_media_files_attributes,
      ).find(([, attr]) => attr.id === mediaId && !attr._destroy)

      if (entry) {
        const [entryKey, attr] = entry
        return {
          ...currentTake,
          take_media_files_attributes: {
            ...currentTake.take_media_files_attributes,
            [Number(entryKey)]: { ...attr, alt_text: altText },
          },
        }
      }

      const mediaKey = nextMediaKey(takeKey)
      return {
        ...currentTake,
        take_media_files_attributes: {
          ...currentTake.take_media_files_attributes,
          [mediaKey]: { id: mediaId, alt_text: altText },
        },
      }
    })
  }

  function updateNewAltText(
    takeKey: number,
    mediaKey: number,
    altText: string,
  ) {
    updateTake(takeKey, (currentTake) => ({
      ...currentTake,
      take_media_files_attributes: {
        ...currentTake.take_media_files_attributes,
        [mediaKey]: {
          ...currentTake.take_media_files_attributes[mediaKey],
          alt_text: altText,
        },
      },
    }))
  }

  function updateTakeBody(takeKey: number, body: string) {
    updateTake(takeKey, (currentTake) => ({ ...currentTake, body }))
  }

  function updateCodeLanguage(takeKey: number, code_language: string) {
    updateTake(takeKey, (currentTake) => ({ ...currentTake, code_language }))
  }

  function updateCodeSnippet(takeKey: number, code_snippet: string) {
    updateTake(takeKey, (currentTake) => ({ ...currentTake, code_snippet }))
  }

  function updateQuotedTake(takeKey: number, quotedTakeId: number | null) {
    updateTake(takeKey, (currentTake) => ({
      ...currentTake,
      quoted_take_id: quotedTakeId,
    }))
  }

  return {
    takeEntries,
    addTake,
    removeTake,
    moveTake,
    changeTakeType,
    handleFiles,
    removeExistingMedia,
    removeNewMedia,
    updateExistingAltText,
    updateNewAltText,
    updateTakeBody,
    updateCodeLanguage,
    updateCodeSnippet,
    updateQuotedTake,
    visibleImageCountForTake,
    CODE_TAKE_TYPE,
  }
}
